import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { cn } from "@/lib/cn";

type Toast = { id: string; title: string; description?: string; variant?: "default" | "error" };
type Ctx = { push: (t: Omit<Toast, "id">) => void };
const ToastCtx = React.createContext<Ctx>({} as Ctx);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const push = React.useCallback((t: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((s) => [...s, { ...t, id }]);
  }, []);
  return (
    <ToastPrimitive.Provider swipeDirection="right">
      <ToastCtx.Provider value={{ push }}>
        {children}
        {toasts.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            className={cn(
              "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all data-[state=open]:animate-fade-up",
              t.variant === "error"
                ? "border-destructive/50 bg-destructive text-destructive-foreground"
                : "border-border bg-card text-card-foreground",
            )}
            duration={t.variant === "error" ? 6000 : 3500}
            onOpenChange={(open) => !open && setToasts((s) => s.filter((x) => x.id !== t.id))}
          >
            <div className="flex flex-col gap-1">
              <ToastPrimitive.Title className="text-sm font-semibold">{t.title}</ToastPrimitive.Title>
              {t.description ? <ToastPrimitive.Description className="text-xs opacity-90">{t.description}</ToastPrimitive.Description> : null}
            </div>
            <ToastPrimitive.Close className="rounded p-1 hover:opacity-80">×</ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
      </ToastCtx.Provider>
    </ToastPrimitive.Provider>
  );
}

export const ToastViewport = () => (
  <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-50 flex max-h-screen w-full flex-col gap-2 sm:max-w-sm" />
);

export const useToast = () => React.useContext(ToastCtx);
